import React from "react";
import AuthService from "src/auth/auth-service";
import { Domain, getBartLambdaConfig } from "src/config/BartLambdaConfig";

import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  Modal,
  NonCancelableCustomEvent,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Auth } from "aws-amplify";
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, PutCommand } from "@aws-sdk/lib-dynamodb";
import { FullPageTitle } from "src/components/header/title";
import GetPlan from "src/components/remappingArdToPlanPage/get-plan";
import { Loader } from "src/components/remappingArdToPlanPage/loader";
import { InitDomain } from "src/context/domain/domainContext";
import Collapsible from "src/pages/commons/collapsible";
import { inputValidation } from "src/commons/input-validation";
import {
  FuseAccountingCentralDatastoreClient,
  GetPlanCommand,
  MigrationTargetStage,
  UpdatePlanCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";

interface PutRequestParameters {
  planIdKey: string;
  planArd: string;
}

export class AppPutPlanArd extends React.Component<
  {},
  {
    ldap: string;
    id: string;
    ardId: string;
    fusenEnabled: boolean;
    glLineDescriptionPartnerName: string;
    fusenEnableDate: string;
    migrationTargetStage: string;
    storeArd: string;
    keyword: string;
    searched: boolean;
    found: boolean;
    isModalOpen: boolean;
    reasoning: string;
    payload: any;
  }
> {
  authObj: AuthService;

  constructor(props: any) {
    super(props);
    this.authObj = new AuthService();
    this.state = {
      ldap: "",
      id: "",
      ardId: "",
      fusenEnabled: false,
      glLineDescriptionPartnerName: "",
      fusenEnableDate: "",
      migrationTargetStage: "",
      storeArd: "",
      keyword: "",
      searched: false,
      found: false,
      isModalOpen: false,
      reasoning: "",
      payload: "init",
    };
    this.put = this.put.bind(this);
    this.setArd = this.setArd.bind(this);
    this.setValue = this.setValue.bind(this);
    this.search = this.search.bind(this);
    this.setOpenModal = this.setOpenModal.bind(this);
    this.setCloseModal = this.setCloseModal.bind(this);
  }

  setValue(value: string) {
    this.setState({ keyword: value });
  }

  async getPlan(domain: Domain) {
    try {
      const credentials = await Auth.currentCredentials();
      const essentialCredentials = Auth.essentialCredentials(credentials);

      const facdClient = new FuseAccountingCentralDatastoreClient({
        ...getFacdConfig(domain), // Get the configuration for the domain
        credentials: essentialCredentials, // Pass essential credentials
      });

      const getPlanCommand = new GetPlanCommand({
        planId: this.state.keyword,
      });

      console.log(
        "Starting FACD getPlan operation with getPlanCommand:",
        getPlanCommand,
      );

      const response = await facdClient.send(getPlanCommand);
      console.log("Response received from facdClient:", response);
      let planData = response.plan;
      let dateWithMSeconds = planData?.fusenEnableDate || "";
      this.setState({
        ...this.state,
        id: planData?.id || "",
        ardId: planData?.ardId || "",
        fusenEnableDate: dateWithMSeconds,
        fusenEnabled: planData?.fusenEnabled || false,
        migrationTargetStage: planData?.migrationTargetStage || "",
        glLineDescriptionPartnerName:
          planData?.glLineDescriptionPartnerName || "",
        storeArd: planData?.ardId || "",
        payload: planData,
      });
    } catch (error: any) {
      console.error("An error occurred in getPlan:", error);
      this.setState({
        ...this.state,
        payload: error.message,
      });
    } finally {
      console.log("Get plan operation completed.");
    }
  }

  async search(domain: Domain) {
    this.setState({
      searched: true,
      payload: "init",
    });
    await this.getPlan(domain);
  }

  setOpenModal() {
    this.setState({ isModalOpen: true });
  }
  setCloseModal() {
    this.setState({ isModalOpen: false });
  }

  async putPlan(domain: Domain) {
    try {
      const credentials = await Auth.currentCredentials();
      const essentialCredentials = Auth.essentialCredentials(credentials);

      const facdClient = new FuseAccountingCentralDatastoreClient({
        ...getFacdConfig(domain), // Get the configuration for the domain
        credentials: essentialCredentials, // Pass essential credentials
      });

      const updatePlanCommand = new UpdatePlanCommand({
        id: this.state.keyword,
        ardId: this.state.ardId,
        glLineDescriptionPartnerName: this.state.glLineDescriptionPartnerName,
        fusenEnabled: this.state.fusenEnabled,
        migrationTargetStage: this.state
          .migrationTargetStage as MigrationTargetStage,
        fusenEnableDate: this.state.fusenEnableDate,
      });

      console.log(
        "Starting FACD updatePlan operation with updatePlanCommand:",
        updatePlanCommand,
      );

      const response = await facdClient.send(updatePlanCommand);
      console.log("Response received from facdClient:", response);
      let planData = response.plan;
      this.setState({
        ...this.state,
        storeArd: planData?.ardId || "",
        payload: planData,
      });
    } catch (error: any) {
      console.error("An error occurred in UpdatePlan:", error);
      this.setState({
        ...this.state,
        payload: error.message,
      });
    } finally {
      console.log("Update plan operation completed.");
    }
  }

  setArd(event: NonCancelableCustomEvent<any>) {
    this.setState({ ["ardId"]: event.detail.value.trim() } as any);
  }

  setReasoning(event: NonCancelableCustomEvent<any>) {
    this.setState({ ["reasoning"]: event.detail.value } as any);
  }

  componentDidMount() {
    const fetchData = async () => {
      // Run authentication
      await this.authObj.authenticate();
      this.setState({ ldap: this.authObj.ldap });
    };
    fetchData();
  }

  async put(domain: Domain) {
    this.setState({
      found: true,
    });
    this.setCloseModal();
    this.setState({
      payload: "init",
    });
    const parameters: PutRequestParameters = {
      planIdKey: this.state.id,
      planArd: this.state.ardId,
    };

    Auth.currentCredentials().then((credentials) => {
      const client = new DynamoDBClient({
        credentials: Auth.essentialCredentials(credentials),
        region: getBartLambdaConfig(domain).awsRegion,
      });

      const docClient = DynamoDBDocumentClient.from(client);
      const moment = require("moment");
      const ddbPut = async () => {
        const command = new PutCommand({
          TableName: "BARTAuditHistoryTable",
          Item: {
            userId: this.authObj.userId,
            requestTime: moment().format("YYYY-MM-DD HH:mm:ss"),
            operationType: "Re-Mapping ARD to Plan",
            additionalInfo: "Plan Id: " + this.state.id,
            reasoning: this.state.reasoning,
          },
        });
        await docClient.send(command);
      };
      ddbPut();
    });
    return await this.putPlan(domain);
  }

  render() {
    if (!this.state.ldap.includes("bart-admin")) {
      return (
        <div>
          <FullPageTitle
            title={"Re-Mapping ARD to Plan"}
            description={"Re-Maps the ARD of a Fuse Plan"}
          />
          <SpaceBetween size="l" />
          <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
            <Alert
              header="Only authorized users can re-map ARD to a plan!"
              type="error"
            ></Alert>
          </Grid>
        </div>
      );
    }
    if (!this.state.searched) {
      return (
        <GetPlan
          keyword={this.state.keyword}
          setValue={this.setValue}
          onClickSearch={this.search}
        />
      );
    }
    if (this.state.payload == "init") {
      return <Loader />;
    }
    if (!this.state.payload.id) {
      return (
        <GetPlan
          error={this.state.payload}
          keyword={this.state.keyword}
          setValue={this.setValue}
          onClickSearch={this.search}
        />
      );
    }

    const { id, ardId } = this.state;

    return (
      <InitDomain.Consumer>
        {(context) => (
          <>
            <FullPageTitle
              title={"Re-Mapping ARD to Plan"}
              description={"Re-Maps the ARD of a Fuse Plan"}
            />
            <SpaceBetween size="l">
              <form onSubmit={(event) => event.preventDefault()}>
                <Form
                  actions={
                    <SpaceBetween size="xs" direction="horizontal">
                      <Button variant="primary" onClick={this.setOpenModal}>
                        Re-Map ARD to Plan
                      </Button>
                    </SpaceBetween>
                  }
                  errorIconAriaLabel="Error"
                >
                  <SpaceBetween size="l">
                    <Container
                      id="origin-panel"
                      className="custom-screenshot-hide"
                      header={<Header variant="h2">Link ARD to a Plan</Header>}
                    >
                      <SpaceBetween size="l">
                        <FormField
                          label="Plan Id"
                          description="The Fuse Plan ID"
                          i18nStrings={{ errorIconAriaLabel: "Error" }}
                        >
                          <Input
                            placeholder="Please enter plan id"
                            ariaRequired={true}
                            value={id}
                            disabled={true}
                          />
                        </FormField>
                        <FormField
                          label="ARD Id"
                          description="The ARD ID associated with the plan"
                          i18nStrings={{ errorIconAriaLabel: "Error" }}
                        >
                          <Input
                            placeholder="Please enter ARD id"
                            ariaRequired={true}
                            value={ardId}
                            onChange={(event) => this.setArd(event)}
                          />
                        </FormField>
                        <FormField
                          label="Re-mapping Reasoning"
                          description="Why you are performing this re-mapping"
                          i18nStrings={{ errorIconAriaLabel: "Error" }}
                        >
                          <Input
                            placeholder="Please enter your reason to perform a re-mapping"
                            ariaRequired={true}
                            value={this.state.reasoning}
                            onChange={(event) => this.setReasoning(event)}
                          />
                        </FormField>
                      </SpaceBetween>
                    </Container>
                  </SpaceBetween>
                </Form>
                <Modal
                  visible={this.state.isModalOpen}
                  onDismiss={this.setCloseModal}
                  header={"Review changes"}
                  closeAriaLabel="Close dialog"
                  footer={
                    <Box float="right">
                      <SpaceBetween size="xs" direction="horizontal">
                        {inputValidation(this.state.reasoning) ||
                        inputValidation(this.state.ardId) ? (
                          <Button
                            variant="primary"
                            onClick={this.setCloseModal}
                          >
                            Update Inputs
                          </Button>
                        ) : (
                          <div>
                            <Button variant="link" onClick={this.setCloseModal}>
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => this.put(context.domain)}
                              data-testid="submit"
                            >
                              Update
                            </Button>
                          </div>
                        )}
                      </SpaceBetween>
                    </Box>
                  }
                >
                  <SpaceBetween size="m">
                    {inputValidation(this.state.reasoning) ||
                    inputValidation(this.state.ardId) ? (
                      <Box variant="span">
                        <Alert
                          type="error"
                          header="Invalid Input! ARD id and reasoning fields cannot be empty"
                        ></Alert>
                      </Box>
                    ) : (
                      <Box variant="span">
                        Permanently delete distribution{" "}
                        <Box variant="span" fontWeight="bold">
                          <section>
                            {this.state.storeArd !== this.state.ardId && (
                              <p>
                                Plan ARD Id has been changed from{" "}
                                {this.state.storeArd}&nbsp;to {this.state.ardId}
                              </p>
                            )}
                          </section>
                        </Box>
                        <Alert statusIconAriaLabel="Info">
                          You can't undo this action.
                        </Alert>
                      </Box>
                    )}
                  </SpaceBetween>
                </Modal>
              </form>
              {this.state.payload === "invalid-ard" ? (
                <div className="alert-box">
                  <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
                    <Alert header="Ard not found!" type="error"></Alert>
                  </Grid>
                </div>
              ) : (
                <Collapsible title={"Plan Preview"} open={false}>
                  <pre className="sourceInfo">
                    {JSON.stringify(this.state.payload, null, 2)}
                  </pre>
                </Collapsible>
              )}
            </SpaceBetween>
          </>
        )}
      </InitDomain.Consumer>
    );
  }
}

export default AppPutPlanArd;
